import decodeHTMLEntities from "../../../util/decodeHTMLEntities";

// Matches both self-closing and child-bearing <follow-up> tags
const FOLLOW_UP_TAG_REGEX =
  /<follow-up\s([^>]*?)(?:\/>|>([\s\S]*?)<\/follow-up>)/gi;

/**
 * Removes all <follow-up> tags (and their inner content) from an HTML string.
 * Use this to prevent the label text from appearing inline in the rendered message.
 *
 * @param {string} html - The raw HTML string to clean
 * @returns {string} HTML with all <follow-up> blocks removed
 */
export const stripFollowUpTags = (html) => {
  if (!html || typeof html !== "string") {
    return html;
  }
  return html.replace(FOLLOW_UP_TAG_REGEX, "").trim();
};

/**
 * Extracts follow-up prompt values from <follow-up> tags in an HTML string.
 *
 * Supports both `prompt="value"` and `prompt='value'` attribute styles, as well
 * as self-closing tags and tags with children.
 *
 * @param {string} html - The raw HTML string to scan
 * @returns {{ label: string, prompt: string }[]} Array of follow-up prompt objects
 */
export const extractFollowUpPrompts = (html) => {
  if (!html || typeof html !== "string") {
    return [];
  }

  const results = [];
  // Match <follow-up ...prompt="..."...>...children...</follow-up> or self-closing
  const tagRegex = new RegExp(FOLLOW_UP_TAG_REGEX.source, "gi");

  let tagMatch;
  while ((tagMatch = tagRegex.exec(html)) !== null) {
    const attrs = tagMatch[1];
    const children = tagMatch[2];

    // Extract prompt attribute value (double or single quotes), supporting special characters
    const promptMatch = attrs.match(/prompt=(["'])([\s\S]*?)\1/);
    if (!promptMatch) {
      continue;
    }

    const prompt = decodeHTMLEntities(promptMatch[2]);

    // Use children text as button label, falling back to the prompt itself.
    // Strip HTML tags from children to get plain text.
    const rawLabel = children
      ? children.replace(/<[^>]*>/g, "").trim() || prompt
      : prompt;
    const label = decodeHTMLEntities(rawLabel);

    results.push({ label, prompt });
  }

  return results;
};
